import React, { PureComponent, ComponentType } from 'react';
import cx from 'classnames';
import _ from 'lodash';
import { withPropsOnChange } from 'recompose';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';

import { Actions, createDefaultStructure } from '../../redux/Formula';
import { LS } from '../../utils/LocalStorage';
import DB from '../../utils/IndexDB';
import { errorHandler, parseQuery } from '../../utils/utils';
import { cleanFormula, structIterate } from '../../utils/formula';
import validateFormula from '../../utils/validateFormula';

import { AddNewItem } from '../../ui';
import ExpressionSelector from '../../expressions/ExpressionSelector/ExpressionSelector';
import Exclamation from '../../components/Exclamation';
import ProductPreview from './ProductPreview';
import FunctionManager from './FunctionManager/FunctionManager';
import TabContent from '../../components/TabContent';
import CheckerMessages from './CheckerMessages/CheckerMessages';
import ProductEditor from './ProductEditor';
import Nav from './components/Nav';
import BuilderContext, { BuilderContextProvider } from 'src/utils/BuilderContext';
import memoizeOne from 'memoize-one';
import { api } from '@pinto/api-client';
import ProductAutoComplete from 'src/components/autocomplete/ProductAutocomplete';
import { GlobalResourceDrawer } from 'src/components/ResourceDrawer';
import { Spinner } from '@blueprintjs/core';
import useExternalData from 'src/hooks/useExternalData';
import type { FormulaNode, FormulaState, RawFormulaData, UiFormulaState } from '../../typeDefs';
import type { RootState } from '../../redux/store';

const AnyProductEditor = ProductEditor as ComponentType<any>;
const AnyTabContent = TabContent as ComponentType<any>;
const AnyNav = Nav as ComponentType<any>;
const AnyProductPreview = ProductPreview as ComponentType<any>;

declare global {
    interface Window {
        Builder?: unknown;
        LS?: unknown;
        DB?: unknown;
        _?: unknown;
    }
}

interface UrlParts {
    firstTab: string[];
    secondTab: string[];
    slug: string | null;
    [key: string]: unknown;
}

interface BuilderPageProps {
    formula: FormulaState;
    setFormula: (data: unknown, options?: unknown) => void;
    undo: () => void;
    setLog: (log: unknown) => void;
    setOptions: (options: unknown) => void;
    setPresets: (value: unknown) => void;
    toggleAll: (value: unknown) => void;
    setIn: (path: string, value: unknown) => void;
    setCollapseDepth: (depth: number) => void;
    checkerMessages?: unknown;
    testsRun?: unknown;
    history: { push: (path: string) => void };
    match: { params: Record<string, string> };
}

interface BuilderPageState {
    usedProps: string[] | null;
    currentConfig: Record<string, unknown>;
    initialized: boolean;
    edit: boolean;
    extraColumn: boolean;
    ignoreGlobalFilter: boolean;
    preview?: { builder: any; checker: any } | null;
    result?: any;
    stats?: any;
    status?: { running?: boolean };
}

class BuilderPage extends PureComponent<BuilderPageProps, BuilderPageState> {
    static contextType = BuilderContext;

    // `declare context` clashes with the legacy-decorator babel transform, so read
    // the context through a typed accessor instead.
    get ctx(): React.ContextType<typeof BuilderContext> {
        return this.context as React.ContextType<typeof BuilderContext>;
    }

    state: BuilderPageState = {
        usedProps: [],
        currentConfig: {},
        initialized: false,
        edit: false,
        extraColumn: !!LS.get('extraColumn', true),
        ignoreGlobalFilter: !!LS.get('ignoreGlobalFilter', false),
    };

    constructor(props: BuilderPageProps) {
        super(props);

        window.Builder = this;
        window.LS = LS;
        window._ = _;
        window.DB = DB;
    }

    async componentDidMount() {
        const [, , , slug] = window.location.pathname.split('/');

        if (slug) {
            try {
                const list = await ProductAutoComplete.fetchItems(slug);
                this.ctx.setPartialProduct(list[0]);
                this.ctx.setLoadingState(null);
            } catch (ex) {
                console.error(ex);
            }
        }

        setTimeout(() => {
            this.ctx.onProductChange(() => this.run({}));
        }, 100);
    }

    UNSAFE_componentWillReceiveProps(nextProps: BuilderPageProps) {
        const options = _.get(nextProps, 'formula.data.options', []);
        if (_.get(this.props, 'formula.data.options') !== options) {
            this.setState({
                currentConfig: _.mapValues(_.keyBy(options, 'key'), 'default'),
            });
        }
    }

    componentDidUpdate(prevProps: BuilderPageProps) {
        const { formula } = this.props;

        if (this.state.initialized && (formula?.data !== prevProps.formula?.data)) {
            DB.put({ id: 'formula', formula: _.pick(formula, ['data', 'history']) });
            this.setState({
                preview: null,
                usedProps: null,
                result: null,
            });
        }
    }

    setFormula(data: unknown, options?: Record<string, unknown>) {
        this.props.setFormula(data, {
            // hideFunctions: true,
            // resetState: true,
            checkerMode: true,
            ...options,
        });
    }

    UNSAFE_componentWillMount() {
        DB.getById('formula').then((stored) => {
            const formula = (stored as { formula?: { data: unknown; history: unknown } } | undefined)?.formula;
            if (formula) this.setFormula(formula.data);
            this.setState({ initialized: true });
        });
    }

    makeUsedProps(props: string[]) {
        return _.uniq(['name', 'slug', 'topology', ...props]);
    }

    async run({ tests = false }: { tests?: boolean } = {}) {
        const { setLog, formula: { data } } = this.props;
        const { pv2 } = this.ctx;
        this.ctx.setLoadingState('running formula');

        if (pv2?.slug) this.goTo({ slug: pv2.slug });

        errorHandler();

        try {
            const res: any = await api.post('/admin/builder-runner', {
                data: {
                    ...(tests ? cleanFormula(data as unknown as RawFormulaData) : { ...cleanFormula(data as unknown as RawFormulaData), tests: undefined }),
                    ignoreGlobalFilter: this.state.ignoreGlobalFilter,
                },
                pv2,
                options: this.state.currentConfig,
            });

            setLog(res);

            this.setState({
                preview: {
                    builder: {
                        ...res.doc,
                        __vars: res.vars,
                        __exports: res.exports,
                        __pv2AddAttributes: res.pv2AddAttributes,
                        __pv2AddAttributesError: res.error || '',
                        __pv2RemoveAttributes: res.pv2RemoveAttributes,
                    },
                    checker: { ...res.doc, __vars: _.get(res, ['checker', 'vars']) },
                },
                usedProps: this.makeUsedProps(res.props),
                result: res,
            });
        } catch (ex) {
            errorHandler(ex);
        }

        this.ctx.setLoadingState(null);
    }

    handleRun = async (opts?: { tests?: boolean }) => {
        const { slug } = this.getUrl();

        if (slug) {
            // todo
        }

        await this.run(opts);
    };

    handleClear = () => {
        const { setLog } = this.props;
        setLog(null);
        this.setState({
            preview: null,
            usedProps: null,
            result: null,
        });
    };

    handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        if (!event.dataTransfer || !event.dataTransfer.files) return;
        const file = event.dataTransfer.files[0];
        if (!file) return;
        const name = file.name.replace(/\.[^.]+$/, '');
        const reader = new FileReader();
        reader.onload = ({ target }) => {
            const formula = { name, ...JSON.parse(String(target?.result)) };

            try {
                validateFormula(formula);
            } catch (ex) {
                errorHandler(ex);
                return;
            }

            this.setFormula(formula);
        };
        reader.readAsText(file);
    };

    handleFileInfoChange = (fileInfo: Record<string, unknown>) => {
        const { formula: { data } } = this.props;

        this.setFormula({
            ...data,
            ...fileInfo,
        });
    };

    handleSelectFormula = (data: any) => {
        this.setFormula({
            ...data,
            structure: JSON.parse(data.structure),
            checker: !data.checker ? undefined : JSON.parse(data.checker),
            filter: !data.filter ? undefined : JSON.parse(data.filter),
            functions: !data.functions ? undefined : JSON.parse(data.functions),
        });
    };

    handleReset = () => {
        this.setFormula({
            name: 'Reset',
            structure: createDefaultStructure(),
        });
    };

    handleColumnToggle = () => {
        const extraColumn = !this.state.extraColumn;
        this.setState({ extraColumn });
        LS.set({ extraColumn });
    };

    goTo(obj: Partial<UrlParts>) { return this.props.history.push(this.getUrlString(obj)); }

    getUrlString(obj: Partial<UrlParts>) {
        const merged = _.defaults({}, obj, this.getUrl()) as UrlParts;

        const { slug } = merged;
        const firstTab = merged.firstTab.join(':');
        const secondTab = merged.secondTab.join(':');

        const q = location.search || '';

        if (slug) return `/${firstTab}/${secondTab}/${slug}${q}`;

        return `/${firstTab}/${secondTab}${q}`;
    }

    getUrl(): UrlParts {
        const { match: { params } } = this.props;

        const defaults: Record<string, string> = {
            firstTab: 'builder',
            secondTab: 'preview',
            slug: '',
        };

        const result = _.defaults({}, params, defaults) as Record<string, any>;
        const availableTabs = this.getAvailableTabs();

        const set = (id: string) => {
            result[id] = (result[id] || '').split(':');
            if (result[id].length < 2 && !availableTabs[result[id][0] as keyof typeof availableTabs]) result[id] = defaults[id].split(':');
        };

        set('firstTab');
        set('secondTab');

        return result as UrlParts;
    }

    getAvailableTabs() {
        const { checkerMessages, formula } = this.props;
        const { preview, stats } = this.state;

        return {
            builder: true,
            functions: true,
            checker: true,
            checkerStats: true,
            options: true,
            tests: true,
            presets: !!_.size(formula.data.options),
            checkerMessages: !!checkerMessages,
            preview: !!preview,
            stats: !!stats,
        };
    }

    goToTabIfNeeded(type: 'firstTab' | 'secondTab', tab: string) {
        const url = this.getUrl();

        if (url.firstTab.includes(tab) || url.secondTab.includes(tab)) return;

        this.goTo({ [type]: [tab] });
    }

    getHasVisibleFunctions = memoizeOne((structure: FormulaNode, checker: FormulaNode | null | undefined) => {
        let hasVisible = false;
        structIterate(
            { $op: 'and', $value: _.filter([structure, checker]) },
            item => {
                hasVisible = hasVisible || (item.$op === 'call' && !item.state?.hidden);
            },
        );

        return hasVisible;
    });

    render() {
        const {
            undo,
            toggleAll,
            formula,
            setPresets,
            checkerMessages,
            setOptions,
            setIn,
            setCollapseDepth,
            match: { params },
        } = this.props;
        const {
            preview,
            usedProps,
            stats,
            status,
            initialized,
            extraColumn,
            result,
            ignoreGlobalFilter,
            edit,
        } = this.state;
        const { pv2 } = this.ctx;
        const { data } = formula;

        if (!initialized) return null;

        const url = this.getUrl();

        const checkParams = (id: string) => _.isEqual((params[id] || '').split(':'), url[id]);

        if (!checkParams('firstTab') || !checkParams('secondTab')) {
            return <Redirect to={this.getUrlString({})} />;
        }

        if (!data) return <div>waiting...</div>;

        const isRunning = !!status && !!status.running;
        const hasVisibleFunctions = this.getHasVisibleFunctions(formula.data.structure, formula.data.checker);

        const isChecker = params.firstTab === 'checker' || params.secondTab === 'checker';

        const contentProps = {
            setIn,
            checkerMessages: checkerMessages,
            formula: formula,
            preview: !preview ? null : (
                isChecker ? preview.checker || preview.builder : preview.builder
            ),
            isChecker,
            stats: stats,
            usedProps: usedProps,
            setOptions: setOptions,
            setPresets,
            handleRun: this.handleRun,
            configProps: {
                options: formula.data.options,
                values: this.state.currentConfig,
                onChange: (currentConfig: Record<string, unknown>) => this.setState({ currentConfig }),
            },
        };

        return <div
            className='builder'
            onDragOver={event => event.preventDefault()}
            onDragStart={event => event.preventDefault()}
            onDragEnter={event => event.preventDefault()}
            onDrop={this.handleDrop}
        >
            {edit && <AnyProductEditor onClose={() => this.setState({ edit: false })} />}
            <AnyNav
                formula={formula as unknown as UiFormulaState}
                preview={!!preview}
                clear={this.handleClear}
                run={() => this.run()}
                toggleEdit={() => this.setState({ edit: !edit })}
                handleFileInfoChange={this.handleFileInfoChange}
                reset={this.handleReset}
                undo={undo}
                hasVisibleFunctions={hasVisibleFunctions}
                toggleFunctions={() => toggleAll(hasVisibleFunctions)}
                toggleExtraColumn={this.handleColumnToggle}
                extraColumn={extraColumn}
                ignoreGlobalFilter={ignoreGlobalFilter}
                toggleIgnoreGlobalFilter={() =>
                    this.setState(
                        { ignoreGlobalFilter: !ignoreGlobalFilter },
                        () => LS.set({ ignoreGlobalFilter: this.state.ignoreGlobalFilter }),
                    )
                }
                collapseDepth={_.get(formula, 'uiOptions.collapseDepth', 6)}
                setCollapseDepth={(val: number) => setCollapseDepth(val || 6)}
                setFormula={this.handleSelectFormula}
            />

            {!formula.errors || !Object.keys(formula.errors).length ? null :
                <div className={'builder-errors'}>
                    You have {Object.keys(formula.errors).length} error(s) in your structure. <br />
                    Look for <Exclamation message='Mouse over will show you the errors' /> bellow to get details
                </div>
            }

            {result && (
                result.filteredOutByGlobalFilter
                    ? <div className='builder-filter-message'>
                        Formula is disabled for this product's taxonomies via the
                        {' '}
                        <a target='_blank' rel='noreferrer' href='https://admin.pinto.co/configs#builder:formula-filters'>builder:formula-filters</a> Config
                    </div>
                    : null
            )}

            <div className={cx('builder-content', formula.log == null && 'builder-with-colors')}>
                {result && result.errors.length &&
                    <div className='builder-schema-errors'>
                        {result.errors.map((e: any, i: number) => (<span key={i}><strong>{e.attributeType}</strong>&mdash;{e.message}</span>))}
                    </div>}
                <div className='builder-content-container'>
                    <Content
                        {...contentProps}
                        selectedTab={url.firstTab}
                        setSelectedTab={(val) => this.goTo({ firstTab: val })}
                        availableTabs={{
                            ...this.getAvailableTabs(),
                            ...(!extraColumn ? null : _.mapValues(_.keyBy(url.secondTab), () => false)),
                        }}
                    />
                    {!extraColumn ? null :
                        <Content
                            {...contentProps}
                            selectedTab={url.secondTab}
                            setSelectedTab={(val) => this.goTo({ secondTab: val })}
                            availableTabs={{ ...this.getAvailableTabs(), ..._.mapValues(_.keyBy(url.firstTab), () => false) }}
                        />
                    }
                </div>
            </div>

            {!parseQuery().debug ? null :
                <pre>
                    <h3>structure:</h3>
                    {JSON.stringify(cleanFormula(data as unknown as RawFormulaData).structure, null, 4)}
                    <h3>functions</h3>
                    {JSON.stringify(data.functions.map(f => cleanFormula(f as unknown as RawFormulaData)), null, 4)}
                    <h3>checker</h3>
                    {JSON.stringify(cleanFormula(data as unknown as RawFormulaData).checker, null, 4)}
                    <h3>tests</h3>
                    {JSON.stringify(_.map(data.tests as unknown[], (t) => cleanFormula(t as unknown as RawFormulaData)), null, 4)}
                </pre>
            }

            <GlobalResourceDrawer />
        </div>;
    }
}

interface ContentProps {
    availableTabs: Record<string, boolean>;
    selectedTab: string[];
    setSelectedTab: (tab: string[]) => void;
    checkerMessages?: unknown;
    formula: FormulaState;
    stats?: any;
    preview?: any;
    usedProps: string[] | null;
    setOptions: (options: unknown) => void;
    configProps: any;
    setPresets: (value: unknown) => void;
    setIn: (path: string, value: unknown) => void;
    isChecker: boolean;
    handleRun: (opts?: { tests?: boolean }) => void;
}

const Content = ({
    availableTabs,
    selectedTab,
    setSelectedTab,
    checkerMessages,
    formula,
    stats,
    preview,
    usedProps,
    setOptions,
    configProps,
    setPresets,
    setIn,
    isChecker,
    handleRun,
}: ContentProps) =>
    <AnyTabContent defaultSelected={selectedTab} onChange={setSelectedTab} className={'Content'}>{
        _.filter([
            {
                id: 'checkerMessages',
                name: <div className='tab-highlight'>Checker Messages</div>,
                render: () => <CheckerMessages data={checkerMessages as any} />,
            },

            {
                id: 'preview',
                name: <div className='tab-highlight'>Preview</div>,
                render: () =>
                    <AnyProductPreview
                        isChecker={isChecker}
                        configProps={configProps}
                        product={preview}
                        usedProps={usedProps || []}
                    />,
            },

            {
                id: 'builder',
                name: 'Builder',
                render: () =>
                    formula.data.filter ?
                        <div>
                            <h3>Filter:</h3>
                            <ExpressionSelector depth={0} path='filter' />

                            <h3>Builder:</h3>
                            <ExpressionSelector depth={0} path='structure' noRemove={true} />
                        </div>
                        :
                        <div>
                            <AddNewItem
                                onClick={() =>
                                    setIn('filter', { $op: 'fn', $value: 'constant', $args: ['true'] })
                                }
                            >+ add a filter</AddNewItem>
                            <ExpressionSelector depth={0} path='structure' noRemove={true} />
                        </div>,
            },

            {
                id: 'functions',
                name: 'Functions',
                render: () => <FunctionManager className='builder-with-colors' />,
            },
        ], x => x && !!availableTabs[x.id])
    }</AnyTabContent>;

const ConnectedBuilderPage = connect(
    (state: RootState) => ({ formula: state.formula }),
    _.pick(Actions, ['setFormula', 'undo', 'setLog', 'setOptions', 'setPresets', 'toggleAll', 'setIn', 'setCollapseDepth']),
)(
    withPropsOnChange(['formula'], ({ formula }: { formula: FormulaState }) => ({
        checkerMessages: _.get(formula, 'checkerRun.messages'),
        testsRun: _.get(formula, 'testsRun', null),
    }))(
        withRouter(BuilderPage as ComponentType<any>) as ComponentType<any>,
    ) as ComponentType<any>,
) as unknown as ComponentType<any>;

const Builder = (props: Record<string, unknown>) => {
    const { done } = useExternalData();

    if (!done) return <Spinner />;

    return <BuilderContextProvider>
        <ConnectedBuilderPage {...props} />
    </BuilderContextProvider>;
};

export default Builder;
